import * as THREE from 'three';
import { SkelAnimPawn } from "./skelanimpawn";
import { DataCache } from "../helpers/datacache";
import { AnimationData } from "../loaders/zmo";
import { MobObject } from "../objects/mobobject";
import { NpcObject } from "../objects/npcobject";
import { GDM } from '../helpers/datamanager';
import { MultiWait } from '../util/multiwait';
import { SkeletonData } from '../loaders/zmd';
import { Mesh } from '../loaders/zms';
import { EffectManager } from '../effects/effectmanager';
import { DamageRender } from '../helpers/damagerender';
import { OrthoSprite } from '../helpers/orthosprite';

let questMarkerTexture: THREE.Texture | undefined;
const QUEST_MARKER_SCALE = 1;

function getQuestMarkerTexture(): THREE.Texture {
    if (questMarkerTexture) {
        return questMarkerTexture;
    }
    const canvas = document.createElement('canvas');
    canvas.width = 32;
    canvas.height = 32;
    const ctx = canvas.getContext('2d')!;
    ctx.fillStyle = '#ffd21f';
    ctx.font = 'bold 28px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('!', 16, 18);
    questMarkerTexture = new THREE.CanvasTexture(canvas);
    return questMarkerTexture;
}

export class NpcPawn extends SkelAnimPawn {
    static motionFileCache: any;

    owner: any;
    charIdx: number;
    nameTag: any;
    questMarker: any;

    constructor(go?: any) {
        super();
        this.charIdx = 0;
        if (go) {
            this.owner = go;
            this.rootObj.name = "NPC_" + go.serverObjectIdx + "_" + go.charIdx;
            this.rootObj.position.copy(go.position);
            this.rootObj.rotation.z = go.direction;
            go.on("damage", (amount: any) => {
                this.newDamage(amount);
            });
        }
    }

    createNamePlate() {
        if (!this.owner) return;
        const texture = ui.createNamePlate(this.owner);
        // Use STB height if available
        let npcHeight = this.owner.stats.getHeight();
        if (npcHeight === 0) {
            npcHeight = this.boundingBox.max.z - this.boundingBox.min.z;
        }
        // Recreate the sprite with new texture
        let material = new THREE.SpriteMaterial({
            map: texture,
            color: 0xffffff,
        });
        material.depthWrite = false;
        const sprite = new OrthoSprite(material);
        sprite.position.set(0, 0, npcHeight);
        sprite.scale.set(texture.image.width, texture.image.height, 1);
        sprite.offset.set(0, -48, 0);
        this.rootObj.add(sprite);
        this.nameTag = sprite;

        // Keep a selected monster's name and HP visible after the pointer leaves.
        const isMob =
            this.owner instanceof MobObject &&
            !(this.owner instanceof NpcObject);
        if (isMob) {
            let hovered = false;
            const updateVisibility = () => {
                sprite.visible = !this.owner.isDead && (hovered || this.owner.selected);
            };
            updateVisibility();

            this.owner.on("hover_start", () => {
                hovered = true;
                updateVisibility();
            });

            this.owner.on("hover_end", () => {
                hovered = false;
                updateVisibility();
            });

            this.owner.on("selected", updateVisibility);
            this.owner.on("deselected", updateVisibility);
            this.owner.on("died", () => {
                sprite.visible = false;
            });
        }
    }

    setQuestMarker(hasQuest: boolean) {
        if (this.questMarker) {
            this.rootObj.remove(this.questMarker);
            this.questMarker = undefined;
        }
        if (!hasQuest) return;

        const material = new THREE.SpriteMaterial({
            map: getQuestMarkerTexture(),
            depthWrite: false,
        });
        const npcHeight = this.owner?.stats?.getHeight?.() || (this.boundingBox.max.z - this.boundingBox.min.z);
        // Quest markers live in world space so perspective makes them smaller
        // as the camera moves away. Nameplates intentionally remain OrthoSprites.
        const sprite = new THREE.Sprite(material);
        sprite.position.set(0, 0, npcHeight + 0.6);
        sprite.scale.set(QUEST_MARKER_SCALE, QUEST_MARKER_SCALE, 1);
        this.rootObj.add(sprite);
        this.questMarker = sprite;
    }

    _getMotionData(motionIdx: any, callback: any) {
        GDM.get(
            "npc_chars",
            function (this: any, charData: any) {
                let char = charData.characters[this.charIdx];
                if (!char) {
                    console.warn("getMotionData failed");
                    return;
                }
                const animIdx = char.animations[motionIdx];
                const motionFile = charData.animations[animIdx];
                NpcPawn.motionFileCache.get(motionFile, callback);
            }.bind(this)
        );
    }

    playAttackMotion(onFinish: any) {
        this.playMotion(NPCANI.ATTACK, 1, 1, (anim: any) => {
            const cooldownMs = Math.max(500, Math.floor(200000 / this.owner.stats.getAttackSpeed()));
            // MonsterProcess runs once per second, so attacks can only land
            // on whole-second ticks even if the raw cooldown is shorter.
            const hitIntervalMs = Math.ceil(cooldownMs / 1000) * 1000;
            anim.timeScale = Math.max(1, anim.length * 1000 / hitIntervalMs);
            anim.once("finish", onFinish);
        }, () => !this.owner?.isDead);
    }

    playRunMotion() {
        this.playMotion(NPCANI.RUN, 1.0, 0, undefined, () => !this.owner?.isDead);
    }

    playIdleMotion() {
        this.playMotion(NPCANI.STOP, 1.0, 0, undefined, () => !this.owner?.isDead);
    }

    playDieMotion(onFinish?: () => void) {
        this.playMotion(NPCANI.DIE, 1.0, 1, function (anim: any) {
            if (onFinish) {
                anim.once("finish", onFinish);
            }
        });
    }

    playSkillMotion(motionIdx: number, onFinish?: () => void) {
        this.playMotion(motionIdx, 1.0, 1, function (anim: any) {
            if (onFinish) {
                anim.once("finish", onFinish);
            }
        }, () => !this.owner?.isDead);
    }

    _addEffectToBone(boneIdx: any, effectPath: any, callback: any) {
        if (boneIdx >= this.skel.dummies.length) {
            console.warn(
                "Attempted to add effect to invalid dummy ",
                boneIdx,
                this.skel.bones.length,
                this.skel.dummies.length
            );
            return;
        }
        EffectManager.loadEffect(
            effectPath,
            function (this: any, effect: any) {
                if (effect) {
                    this.skel.dummies[boneIdx].add(effect.rootObj);
                    effect.play();
                }
            }.bind(this)
        );
    }

    _setModel(charData: any, modelMgr: any, charIdx: any, callback?: () => void) {
        const char = charData.characters[charIdx];
        if (!char) {
            console.warn("Attempted to use npc character which does not exist");
            if (callback) callback();
            return;
        }
        const skelPath = charData.skeletons[char.skeletonIdx];
        const waitAll = new MultiWait();
        const waitSkeleton = waitAll.one();
        SkeletonData.load(skelPath, (zmdData: any) => {
            this._setSkeleton(zmdData);
            const charSkel = this.skel;
            // Keep the root bone under rootObj. All parts share one bind pose,
            // regardless of the order in which their mesh requests finish.
            this.rootObj.updateMatrixWorld(true);
            charSkel.calculateInverses();
            const bindMatrix = this.rootObj.matrixWorld.clone();
            const charModels = char.models;
            for (let i = 0; i < charModels.length; ++i) {
                const model = modelMgr.data.models[charModels[i]];
                for (let j = 0; j < model.parts.length; ++j) {
                    ((part, waitPart) => {
                        if (
                            part.position ||
                            part.rotation ||
                            part.scale ||
                            part.axisRotation
                        ) {
                            console.warn(
                                "NPC Character part has invalid transform data."
                            );
                        }
                        const material = modelMgr._createMaterial(
                            part.materialIdx
                        );
                        const meshPath = modelMgr.data.meshes[part.meshIdx];
                        Mesh.load(meshPath, (geometry: any) => {
                            const charPartMesh = new THREE.SkinnedMesh(
                                geometry,
                                material
                            );
                            this.rootObj.add(charPartMesh);
                            // Passing the shared bind matrix prevents bind()
                            // from recalculating inverses for each arriving part.
                            charPartMesh.bind(charSkel, bindMatrix);
                            this._addBoundsGeometry(charPartMesh);
                            waitPart();
                        });
                    })(model.parts[j], waitAll.one());
                }
            }
            for (let e = 0; e < char.effects.length; ++e) {
                const effectPath = charData.effects[char.effects[e].effectIdx];
                const boneIdx = char.effects[e].boneIdx;
                this._addEffectToBone(boneIdx, effectPath, undefined);
            }
            waitSkeleton();
        });
        waitAll.wait(() => {
            if (callback) callback();
        });
    }

    setModel(charIdx: any, callback: any) {
        this.charIdx = charIdx;
        GDM.get(
            "npc_chars",
            "npc_models",
            (charList: any, modelList: any) => {
                this._setModel(charList, modelList, charIdx, callback);
            }
        );
    }

    newDamage(amount: any) {
        DamageRender.add(
            amount,
            this.rootObj.localToWorld(new THREE.Vector3(0, 0, 2.4))
        );
    }

    setScale(scale: any) {
        scale = scale * ZZ_SCALE_IN;
        this.rootObj.scale.set(scale, scale, scale);
    }
}

const NPCANI = {
    STOP: 0,
    MOVE: 1,
    ATTACK: 2,
    HIT: 3,
    DIE: 4,
    RUN: 5,
    CACTION1: 6,
    SACTION1: 7,
    CACTION2: 8,
    SACTION2: 9,
    ETC: 10,
    MAX: 11,
};

/**
 * Holds a cache of all loaded animation files data.  This is just the data,
 * and not bound to any particular skeleton.
 *
 * @type {DataCache.<AnimationData>}
 */
NpcPawn.motionFileCache = new DataCache(AnimationData);
